import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  IconLoader2,
  IconPhoto,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { Field } from "@/types/Field";
import { AfImageValue, isAfImageValue } from "@/types/AfImage";
import {
  resolveAfImageUrl,
  resolveAfImageUrlFresh,
  invalidateAfImageUrl,
} from "@/lib/utils/af-image-urls";
import {
  ALLOWED_AF_IMAGE_CONTENT_TYPES,
  uploadAfImagePresigned,
} from "@/lib/utils/af-image-upload";

type TCCImageProps = {
  field: Field;
  /** Stored AfImageValue object, or null/"" when the guest has no image. */
  value: unknown;
  isEditing: boolean;
  isSelected: boolean;
  isFilter?: boolean;
  viewOnly?: boolean;
  /** Host field-save channel. Upload confirm pushes the fresh AfImageValue
   *  (deep-equal echo, ignored by the backend merge guard) — UNLESS
   *  `onValuePersisted` is provided, which then replaces this call; remove
   *  pushes "" (blank = clear on the normal guest-save path) — unless
   *  `onImmediateRemove` is provided. */
  onChangeValue: (value: string | string[]) => void;
  /** Called INSTEAD of onChangeValue after a successful upload confirm (the
   *  value is already persisted server-side at that point). Detail sheet:
   *  update BOTH the current form value and the pristine baseline so the Save
   *  button doesn't light up dirty from the echo. Guest list: refresh the row
   *  — the upload must never enter the pending-save batch. */
  onValuePersisted?: (value: AfImageValue) => void;
  /** List-mode remove: called INSTEAD of onChangeValue("") — the host sends a
   *  single-guest update with "" for this field right away and refreshes the
   *  row (no pending-batch involvement). May reject; an error is shown. */
  onImmediateRemove?: () => void | Promise<void>;
  /** Guest whose field this cell edits — REQUIRED for uploading a first image
   *  (an empty value carries no key to derive it from). Threaded by the guest
   *  detail sheet; absent on the create dialog (guest doesn't exist yet). */
  guestId?: string;
  /** True when the cell is hosted by the guest CREATE dialog (guest doesn't
   *  exist yet) — the guest-less edit hint then reads "upload after create".
   *  Everywhere else a guest-less editable cell says "edit in guest details"
   *  (the detail sheet is the only upload surface). */
  isCreateContext?: boolean;
  height?: string;
  border?: string;
  rounded?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
};

/** Parse eventId/guestId out of a stored R2 key
 *  (`af-images/{eventId}/{guestId}/{fieldLower}/{file}`). */
function idsFromKey(key: string): { eventId: string; guestId: string } | null {
  const parts = key.split("/");
  if (parts.length < 5 || parts[0] !== "af-images") return null;
  return { eventId: parts[1], guestId: parts[2] };
}

/**
 * Cell renderer for IMAGE-typed additional fields.
 *
 * Read mode: ~28px rounded thumbnail resolved through the batched
 * af-images/urls resolver; click opens a lightbox preview. Edit mode (guest
 * detail sheet, where `guestId` is threaded): upload / replace via the
 * presign→PUT→confirm chain (af-image-upload.ts), remove = "" through the
 * host's normal field-save path. Without a guest (create dialog) it shows the
 * "upload after create" hint — the value is only settable once the guest
 * exists. Filter mode renders nothing: IMAGE is filterable only via the
 * valueless is_empty / is_not_empty operators.
 */
export const TCCImage: React.FC<TCCImageProps> = ({
  field,
  value,
  isEditing,
  isSelected,
  isFilter,
  viewOnly,
  onChangeValue,
  onValuePersisted,
  onImmediateRemove,
  guestId,
  isCreateContext,
  height = "h-9",
  border = "border outline-quaternary",
  rounded = "rounded-lg",
  surface = "",
  fontSize = "body-3-regular",
  padding = "px-2",
}) => {
  const t = useTranslations("afImage");
  const params = useParams<{ eventId: string }>();
  const image: AfImageValue | null = isAfImageValue(value) ? value : null;
  const keyIds = image ? idsFromKey(image.key) : null;
  const eventId = params?.eventId || keyIds?.eventId || "";
  const effectiveGuestId = guestId || keyIds?.guestId || "";

  const [url, setUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // The thumbnail button that opened the lightbox — focus returns to it on
  // close (Escape / X / backdrop) so keyboard users don't lose their place.
  const previewTriggerRef = useRef<HTMLButtonElement | null>(null);

  const imageKey = image?.key ?? null;
  useEffect(() => {
    let alive = true;
    if (!imageKey || !eventId || !effectiveGuestId) {
      setUrl(null);
      return;
    }
    resolveAfImageUrl(eventId, effectiveGuestId, field.name).then((u) => {
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [imageKey, eventId, effectiveGuestId, field.name]);

  const closePreview = () => {
    setPreviewOpen(false);
    previewTriggerRef.current?.focus();
  };

  const openPreview = async () => {
    // A cached URL near the end of its presign window could expire mid-view —
    // re-resolve when it's older than ~250s before showing it full-size.
    const fresh = await resolveAfImageUrlFresh(
      eventId,
      effectiveGuestId,
      field.name,
    );
    if (fresh) setUrl(fresh);
    setPreviewOpen(true);
  };

  // Escape closes the lightbox (with focus restore), matching dialog norms.
  useEffect(() => {
    if (!previewOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closePreview();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen]);

  // Filter mode: is_empty / is_not_empty are valueless — no value input.
  if (isFilter) return null;

  const thumb = url ? (
    <button
      type="button"
      ref={previewTriggerRef}
      aria-label={t("preview")}
      onClick={(e) => {
        e.stopPropagation();
        void openPreview();
      }}
      className="shrink-0 cursor-zoom-in"
    >
      <img
        src={url}
        alt={field.translation || field.name}
        className="h-7 w-7 rounded-md object-cover border outline-quaternary"
      />
    </button>
  ) : image ? (
    // Value present but URL still resolving (or expired) — neutral placeholder.
    <span className="h-7 w-7 rounded-md surface-tertiary border outline-quaternary flex items-center justify-center shrink-0">
      <IconPhoto size={14} className="font-tertiary" />
    </span>
  ) : null;

  const lightbox =
    previewOpen && url
      ? createPortal(
          <div
            className="fixed inset-0 z-[10050] bg-black/70 flex items-center justify-center cursor-default"
            role="dialog"
            aria-modal="true"
            aria-label={t("preview")}
            onClick={(e) => {
              e.stopPropagation();
              closePreview();
            }}
          >
            <button
              type="button"
              aria-label={t("close")}
              onClick={(e) => {
                e.stopPropagation();
                closePreview();
              }}
              className="absolute top-4 right-4 p-2 rounded-full surface-primary border outline-quaternary"
            >
              <IconX size={18} className="font-secondary" />
            </button>
            <img
              src={url}
              alt={field.translation || field.name}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85dvh] max-w-[90vw] rounded-xl object-contain shadow-2xl"
            />
          </div>,
          document.body,
        )
      : null;

  // ── Edit mode (guest detail sheet) ──
  if (isEditing && isSelected && !viewOnly) {
    // No guest to scope the presign to. In the CREATE dialog the guest simply
    // doesn't exist yet ("upload after create"); on any other guest-less host
    // the detail sheet is the edit surface ("edit in guest details").
    if (!effectiveGuestId || !eventId) {
      return (
        <div
          className={`flex items-center ${height} ${border} ${rounded} ${surface} ${padding} opacity-60`}
        >
          <p className={`${fontSize} font-tertiary truncate`}>
            {isCreateContext ? t("uploadAfterCreate") : t("editInDetails")}
          </p>
        </div>
      );
    }

    const pickFile = (e: React.MouseEvent) => {
      e.stopPropagation();
      setError(null);
      fileInputRef.current?.click();
    };

    const handleFile = async (file: File | undefined) => {
      if (!file || uploading) return;
      setError(null);
      setUploading(true);
      try {
        const result = await uploadAfImagePresigned(
          eventId,
          effectiveGuestId,
          field.name,
          file,
        );
        if (result.ok) {
          invalidateAfImageUrl(eventId, effectiveGuestId, field.name);
          const fresh = await resolveAfImageUrl(
            eventId,
            effectiveGuestId,
            field.name,
          );
          setUrl(fresh);
          if (onValuePersisted) {
            // The confirm already persisted the value server-side — hand it
            // to the host's persisted-channel (detail sheet: sync current +
            // baseline so Save doesn't light dirty; list: refresh the row).
            // NEVER also onChangeValue: in the list that would poison the
            // pending-save batch with an echo the backend nets to noChanges.
            onValuePersisted(result.value);
          } else {
            // Legacy hosts: push the confirmed value into the form state so
            // the next guest save echoes it deep-equal — buildUpdateGuests
            // strips the echo from both sides before its per-key delta, so
            // the key never rides the payload.
            onChangeValue(result.value as unknown as string);
          }
        } else if (result.stage === "validation") {
          setError(result.message === "tooBig" ? t("tooBig") : t("wrongType"));
        } else {
          setError(t("uploadFailed"));
        }
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    const remove = async (e: React.MouseEvent) => {
      e.stopPropagation();
      setError(null);
      if (onImmediateRemove) {
        // List mode: the host clears the field NOW (single-guest "" update +
        // row refresh) — the pending-save batch is never involved.
        setUploading(true);
        try {
          await onImmediateRemove();
          invalidateAfImageUrl(eventId, effectiveGuestId, field.name);
          setUrl(null);
        } catch {
          setError(t("removeFailed"));
        } finally {
          setUploading(false);
        }
        return;
      }
      setUrl(null);
      // Blank = clear through the host's normal field-save path (backend
      // deletes the object + releases storage on save).
      onChangeValue("");
    };

    return (
      <div className="flex flex-col gap-1 w-full">
        <div
          className={`flex items-center gap-2 ${height} ${border} ${rounded} ${surface} ${padding}`}
        >
          {thumb}
          <input
            ref={fileInputRef}
            type="file"
            accept={Array.from(ALLOWED_AF_IMAGE_CONTENT_TYPES).join(",")}
            className="hidden"
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={pickFile}
            className={`flex items-center gap-1.5 ${fontSize} font-secondary hover:font-primary transition-colors disabled:opacity-50`}
          >
            {uploading ? (
              <IconLoader2 size={14} className="animate-spin" />
            ) : (
              <IconUpload size={14} />
            )}
            <span className="truncate">
              {image || url ? t("replace") : t("upload")}
            </span>
          </button>
          {(image || url) && !uploading && (
            <button
              type="button"
              aria-label={t("remove")}
              title={t("remove")}
              onClick={(e) => void remove(e)}
              className="ml-auto p-0.5 font-tertiary hover:font-error transition-colors"
            >
              <IconX size={14} />
            </button>
          )}
        </div>
        {error && <p className="body-3-regular font-error">{error}</p>}
        {lightbox}
      </div>
    );
  }

  // ── Read mode ──
  if (!image) return <></>;
  return (
    <div className="flex items-center">
      {thumb}
      {lightbox}
    </div>
  );
};
